import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonCoverNoteModal from '../../../components/wizard/CommonCoverNoteModal';
import { setFlashToast } from '../../../utils/flashToast';
import * as attorneyGeneralService from '../../../services/attorneyGeneralCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { AttorneyGeneralWizardStep } from './attorneyGeneralConstants';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { formatDateRange, formatDisplayDate } from '../../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import { apiOrLookupLabel } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import { isBriberySubCategory } from './attorneyGeneralConstants';
import { preferredAccessLabels, requiresCourtType } from './attorneyGeneralHelpers';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath } from './AttorneyGeneralWizardShell';
import { wizardField } from '../wizardI18nHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

function partyBodyTypeLabel(v: string): string {
  if (v === 'government') return 'Government Ministry/ Department/Other Unincorporated Body';
  if (v === 'incorporated') return 'Public Corporation/Statutory Board/State Owned Entity/Other Incorporated Body';
  return '—';
}

function inlineFileNames(files: { originalFileName?: string; fileName?: string }[]): string {
  const names = files.map((f) => f.originalFileName?.trim() || f.fileName?.trim()).filter(Boolean);
  return names.length ? names.join(', ') : '—';
}

export default function AttorneyGeneralReviewPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useAttorneyGeneralLookups('review', draft.provinceId, draft.districtId, draft.courtTypeId);

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const rtiAccessLabel = useMemo(
    () =>
      draft.rtiPreferredMethodOfAccessNames.length
        ? draft.rtiPreferredMethodOfAccessNames.join(', ')
        : preferredAccessLabels(draft.rtiPreferredMethodOfAccessIds, lookups.preferredMethodOfAccess).join(', ') || '—',
    [draft, lookups.preferredMethodOfAccess],
  );

  const showHighCourt = isBriberySubCategory(draft.subFileCategoryName);

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'ag-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await attorneyGeneralService.submitAttorneyGeneralRequest(draft);
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? 'Submission failed.', variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: 'Case Request submitted successfully.', variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = attorneyGeneralWizardPath(1, draft.draftRequestId);
  const editTo = (step: AttorneyGeneralWizardStep) => attorneyGeneralWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <CommonCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        showSuccessBanner
        showCaseSource={false}
        onClose={closeCoverNote}
      />
      <div className="bc">
        <Link to={tasksListPath}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        <Link to={reviewCrumbTo}>Create Case Request</Link>
        <span className="bc-sep">/</span>
        Review Before Submission
      </div>
      <div className="pg-title">Review Case Request Details</div>

      <ReviewSection step="Step 01" title="Case Overview" editTo={editTo(1)}>
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Province" value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)} />
            <ReviewField label="District" value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)} />
            <ReviewField label="Case Source" value={draft.caseSource} span={2} />
            {showHighCourt ? (
              <ReviewField label="High Court Name" value={apiOrLookupLabel(draft.highCourtName, draft.highCourtId, lookups.highCourts)} />
            ) : null}
            <ReviewField label="Magistrate Court Name" value={apiOrLookupLabel(draft.magistrateCourtName, draft.magistrateCourtId, lookups.magistrateCourts)} />
            <ReviewField label="Police Station" value={apiOrLookupLabel(draft.policeStationName, draft.policeStationId, lookups.policeStations)} />
            <ReviewField label="Country Involved" value={apiOrLookupLabel(draft.countryInvolvedName, draft.countryInvolvedId, lookups.countries)} />
            <ReviewField label="Direction of Request" value={draft.directionOfRequest} span={2} />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Has previous advice been received?" value={yesNoLabel(draft.hasPreviousAdvice)} />
            {draft.hasPreviousAdvice === 'yes' ? (
              <ReviewField label="Previous Advice Attachment" value={inlineFileNames(draft.previousAdviceUploads)} />
            ) : null}
            <ReviewField label="Connected Advice Case?" value={yesNoLabel(draft.hasConnectedAdviceCase)} />
            {draft.hasConnectedAdviceCase === 'yes' ? (
              <ReviewField label="Connected Advice Case Details" value={draft.connectedAdviceDetails} span={2} />
            ) : null}
            <ReviewField label="Has action been instituted?" value={yesNoLabel(draft.hasActionInstituted)} />
            {requiresCourtType(draft.hasActionInstituted) ? (
              <>
                <ReviewField label="Court Type" value={draft.courtTypeName || draft.courtTypeId} />
                {draft.courtTypeOther ? <ReviewField label="Court Type (Other)" value={draft.courtTypeOther} /> : null}
              </>
            ) : null}
            {draft.hasActionInstituted === 'yes' ? (
              <ReviewField label="Court Name" value={draft.courtName || '—'} />
            ) : null}
            <ReviewField label="Is this an appeal matter?" value={yesNoLabel(draft.isAppealMatter)} />
            {draft.isAppealMatter === 'yes' ? (
              <>
                <ReviewField label="Original Court" value={draft.originalCourt} />
                <ReviewField label="Judgement/Order" value={inlineFileNames(draft.judgementOrderUploads)} />
              </>
            ) : null}
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={yesNoLabel(draft.hasConnectedMatters)} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ReviewField label="Connected AGD File Reference Number" value={draft.connectedAgdFileRef} />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Party Details" editTo={editTo(2)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Party Body Type" value={partyBodyTypeLabel(draft.partyBodyType)} span={2} />
          <ReviewField label="Plaintiff / Applicant Name" value={draft.plaintiffName} />
          <ReviewField label="Defendant / Respondent Name" value={draft.defendantName} />
          <ReviewField label="Other Involved Institutions" value={draft.otherInvolvedInstitutions} span={2} />
          <ReviewField label="Name of the Legal Officer/ Officer In-Charge" value={draft.legalOfficerName} />
          <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Incident Details" editTo={editTo(3)}>
        <ReviewField label="Subject of the Dispute" value={draft.subjectOfDispute} fullWidth />
        <ReviewGrid cols={4}>
          <ReviewField label="Incident Occurred Date" value={formatDateRange(draft.incidentDateFrom, draft.incidentDateTo)} />
          <ReviewField
            label="Next Step of the Case"
            value={
              draft.nextStepOfCaseOther.trim() ||
              apiOrLookupLabel(draft.nextStepOfCaseName, draft.nextStepOfCaseId, lookups.nextSteps)
            }
          />
          <ReviewField label="Next Date of the Case" value={formatDisplayDate(draft.nextCaseDate)} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Victim Details" editTo={editTo(4)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Name of the Victim" value={draft.victimName} />
          <ReviewField label="Date of Birth" value={formatDisplayDate(draft.victimDob)} />
          <ReviewField label="Gender" value={apiOrLookupLabel(draft.victimGenderName, draft.victimGenderId, lookups.genders)} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 05" title="Offence & Accused Details" editTo={editTo(5)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Date of Offence" value={formatDateRange(draft.offenceDateFrom, draft.offenceDateTo)} />
          <ReviewField label="Place of Offence" value={draft.placeOfOffence} span={3} />
        </ReviewGrid>
        {draft.accused.map((a, i) => (
          <ReviewSubBlock key={a.id} title={`Accused / Suspect ${i + 1}`}>
            <ReviewGrid cols={4}>
              <ReviewField label="Name" value={a.name} />
              <ReviewField
                label="Status"
                value={
                  a.statusOther.trim() ||
                  apiOrLookupLabel('', a.status, lookups.suspectStatuses)
                }
              />
            </ReviewGrid>
          </ReviewSubBlock>
        ))}
      </ReviewSection>

      <ReviewSection step="Step 06" title="Accused Person Details - Extradition & MLA" editTo={editTo(6)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Name of the Accused" value={draft.accusedName} />
          <ReviewField label="Nationality" value={apiOrLookupLabel(draft.nationalityName, draft.nationalityId, lookups.nationalities)} />
          <ReviewField label="Country of Origin" value={apiOrLookupLabel(draft.countryOfOriginName, draft.countryOfOriginId, lookups.countries)} />
          <ReviewField label="Date of Birth" value={formatDisplayDate(draft.accusedDob)} />
          <ReviewField label="Gender" value={apiOrLookupLabel(draft.accusedGenderName, draft.accusedGenderId, lookups.genders)} />
          <ReviewField label="Passport/ID Number" value={draft.passportOrIdNumber} />
          <ReviewField label="Current Custody Status" value={apiOrLookupLabel(draft.custodyStatusName, draft.custodyStatusId, lookups.custodyStatuses)} />
          <ReviewField label="Custodial Authority" value={draft.custodialAuthority} />
          <ReviewField label="Last Known Location" value={draft.lastKnownLocation} span={2} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 07" title="Legal & Extradition" editTo={editTo(7)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Arrest Warrant Available" value={yesNoLabel(draft.arrestWarrantAvailable)} />
          {draft.arrestWarrantAvailable === 'yes' ? (
            <ReviewField label="Warrant Reference" value={draft.warrantReference} />
          ) : null}
          <ReviewField label="Applicable Treaty/Arrangement" value={draft.applicableTreatyArrangement} span={2} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 08" title="Petitioner Details" editTo={editTo(8)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Petitioner Name" value={draft.petitionerName} />
          <ReviewField label="NIC" value={draft.petitionerNic} />
          <ReviewField label="Address" value={draft.petitionerAddress} span={2} />
          <ReviewField label="Contact Number" value={draft.petitionerContact} />
          <ReviewField label="Email Address" value={draft.petitionerEmail} />
          <ReviewField label="Represented by Attorney" value={yesNoLabel(draft.representedByAttorney)} />
          {draft.representedByAttorney === 'yes' ? (
            <>
              <ReviewField label="Attorney Name" value={draft.attorneyName} />
              <ReviewField label="Attorney Contact" value={draft.attorneyContact} />
            </>
          ) : null}
          <ReviewField label="Respondent Name(s)" value={draft.respondentNames} />
          <ReviewField
            label="Respondent Type"
            value={apiOrLookupLabel(draft.respondentTypeName, draft.respondentType, lookups.respondentTypes)}
          />
          <ReviewField label="Respondent Address" value={draft.respondentAddress} span={2} />
          <ReviewField label="Number of Respondents" value={draft.numberOfRespondents} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 09" title="Right to Information" editTo={editTo(9)}>
        <ReviewSubBlock title="Requester Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Name of Requestor" value={draft.rtiRequestorName} />
            <ReviewField label="NIC/ Passport Number" value={draft.rtiNicOrPassport} />
            <ReviewField label="Address" value={draft.rtiAddress} span={2} />
            <ReviewField label="Contact Number" value={draft.rtiContactNumber} />
            <ReviewField label="Email Address" value={draft.rtiEmailAddress} />
            <ReviewField label="Citizen of Sri Lanka" value={yesNoLabel(draft.rtiIsSriLankanCitizen)} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Information Request Details">
          <ReviewField label="Specific Public Authority Name" value={draft.rtiSpecificPublicAuthorityName} fullWidth />
          <ReviewField label="Requested Information Details" value={draft.rtiRequestedInformationDetails} fullWidth />
          <ReviewField label="Information Requested Time Period" value={draft.rtiInformationRequestedTimePeriod} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Additional Information">
          <ReviewField label="Preferred Method of Access" value={rtiAccessLabel} fullWidth />
          <ReviewField
            label="Preferred Language for Response"
            value={apiOrLookupLabel(draft.rtiPreferredLanguageName, draft.rtiPreferredLanguageId, lookups.preferredLanguages)}
          />
          <ReviewField label="Involves Life or Personal Liberty" value={yesNoLabel(draft.rtiInvolvesLifeOrPersonalLiberty)} />
          {draft.rtiInvolvesLifeOrPersonalLiberty === 'yes' ? (
            <ReviewField label="Justification" value={draft.rtiJustification} fullWidth />
          ) : null}
          <ReviewField label="Additional Notes" value={draft.rtiAdditionalNotes} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 10" title="Timeline" editTo={editTo(10)}>
        <ReviewGrid cols={3}>
          <ReviewField label="Urgency Level" value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)} />
          <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
          <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 11" title="Submission" editTo={editTo(11)}>
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField
              label={wizardField(t, 'submittedDivisionUnit')}
              value={profile?.divisionUnit?.trim() || '—'}
            />
            <ReviewField label="Designation" value={profile?.contactDesignation?.trim() || '—'} />
            <ReviewField label="Submitted by" value={profile?.displayName?.trim() || '—'} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={profile?.email?.trim() || '—'} />
            <ReviewField label="Contact Number" value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 12" title="Upload Documents" editTo={editTo(12)}>
        <ReviewSubBlock>
          <CriminalReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <div className="confirm-section ccr-review-confirm">
        <label className="ccr-chk" id="ag-confirm">
          <input
            type="checkbox"
            checked={draft.confirmCorrect}
            disabled={submitting}
            onChange={(e) => {
              setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
              if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
            }}
          />
          <span className="ccr-review-confirm-text">
            <span className="ccr-review-confirm-main">
              I confirm the above information is correct <span className="req">*</span>
            </span>
            <span className="ccr-review-confirm-hint">Please confirm before submitting this request to AGD.</span>
          </span>
        </label>
        {errors.confirmCorrect ? <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div> : null}
      </div>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={12}
        onBack={() => nav(editTo(12))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
